import { number } from '../../node_modules/@intlify/core-base/dist/core-base';
import { useClientState } from '../client/state';
import { getActivePinia } from 'pinia'

const key = 'pipeline.cart';

export const useCart = defineStore('repo.cart', () => {
    const {
        $objectMap,
        $i18n,
        $fbq
    }: any = useNuxtApp();

    // USES
    const matomo = useMatomo();
    const monetary = useMonetary();

    // STORE REFS
    const {
        settings
    } = storeToRefs(useClientState());

    const items = reactive({});
    const culture = ref<string>('en');
    const last = ref();
    const timestamp = ref<number>(Date.now().valueOf());
    const indicativeShipping = ref(null);

    const loading = ref(false);

    const cartSummary = computed(() => ({
        items: $objectMap(items, (entry: any) => ({
            name: entry.article.type === 's' || entry.article.type === 'g'
                ? `${entry.products[0].name} (${entry.products[0].subtitle})${entry.products[0].quantity === 1 ? '' : ` × ${entry.products[0].quantity}`}`
                : entry.article.name,
            image: entry.article.type === 's' || entry.article.type === 'g'
                ? entry.products[0].image
                : '',
            canonical: entry.article.canonical,
            category: entry.article.category,
            code: entry.article.type === 's' || entry.article.type === 'g'
                ? entry.products[0].code
                : entry.article.code,
            quantity: entry.quantity,
            iq: entry.article.type === 's' || entry.article.type === 'g'
                ? entry.products[0].quantity
                : entry.article.quantity,
            statistic: entry.products.flatMap((product: any) => product.prices[settings.value.country].filter((price: any) => price.currency === 'SEK')
                .map((price: any) => monetary.times(monetary.getPriceFor(price, product.quantity, true), entry.quantity)))
                .reduce((a: number, b: number) => a + b),
            prices: {
                item: entry.products.map((product: any) => product.prices[settings.value.country]
                    .filter((price: any) => price.currency === settings.value.currency || price.currency === 'SEK')
                    .reduce((a: any, price: any) => {
                        if (price.currency === settings.value.currency) {
                            a = {
                                ...a,
                                refIncVat: monetary.getRefPriceFor(price, product.quantity, true),
                                refExVat: monetary.getRefPriceFor(price, product.quantity, false),
                                priceIncVat: monetary.getPriceFor(price, product.quantity, true),
                                priceExVat: monetary.getPriceFor(price, product.quantity, false),
                                discountIncVat: monetary.getDiscountPriceFor(price, product.quantity, true),
                                discountExVat: monetary.getDiscountPriceFor(price, product.quantity, false),
                                quantity: product.quantity
                            };
                        }

                        if (price.currency === 'SEK') {
                            a = {
                                ...a,
                                statistic: monetary.getPriceFor(price, product.quantity, true),
                            };
                        }

                        return a;
                    }, {})
                ).reduce((a: any, b: any) => {
                    return ({
                        refPrice: monetary.sum(a.refPrice, settings.value.vat ? b.refIncVat : b.refExVat),
                        price: monetary.sum(settings.value.vat ? a.priceIncVat : a.priceExVat, settings.value.vat ? b.priceIncVat : b.priceExVat),
                        priceIncVat: monetary.sum(a.priceIncVat, b.priceIncVat),
                        priceExVat: monetary.sum(a.priceExVat, b.priceExVat),
                        priceVat: monetary.sum(a.priceVat, monetary.sub(b.priceIncVat, b.priceExVat)),
                        discount: monetary.sum(settings.value.vat ? a.discountIncVat : a.discountExVat, settings.value.vat ? b.discountIncVat : b.discountExVat),
                        discountIncVat: monetary.sum(a.discountIncVat, b.discountIncVat),
                        discountExVat: monetary.sum(a.discountExVat, b.discountExVat),
                        discountVat: monetary.sum(a.discountVat, monetary.sub(b.discountIncVat, b.discountExVat)),

                        each: monetary.sum(a.each, monetary.div(settings.value.vat ? b.priceIncVat : b.priceExVat, b.quantity)),
                        quantity: monetary.sum(a.quantity, b.quantity),
                        statistic: monetary.div(monetary.sum(a.statistic, b.statistic), b.quantity)
                    });
                }, {
                    refPrice: 0,
                    price: 0,
                    each: 0,
                    quantity: 0,

                    priceIncVat: 0,
                    priceExVat: 0,
                    priceVat: 0,
                    discountIncVat: 0,
                    discountExVat: 0,
                    discountVat: 0,
                    statistic: 0
                }),
                get total() {
                    return {
                        refPrice: monetary.times(this.item.refPrice, entry.quantity),
                        price: monetary.times(this.item.price, entry.quantity),
                        discount: monetary.times(this.item.discount, entry.quantity),
                        quantity: monetary.times(this.item.quantity, entry.quantity),
                        priceIncVat: monetary.times(this.item.priceIncVat, entry.quantity),
                        priceExVat: monetary.times(this.item.priceExVat, entry.quantity),
                        priceVat: monetary.times(this.item.priceVat, entry.quantity),
                        discountIncVat: monetary.times(this.item.discountIncVat, entry.quantity),
                        discountExVat: monetary.times(this.item.discountExVat, entry.quantity),
                        discountVat: monetary.times(this.item.discountVat, entry.quantity)
                    };
                }
            },
            meta: {
                discount: {
                    name: entry.article.type === 's' || entry.article.type === 'g'
                        ? [...new Set(entry.products.map((product: any) => product.meta.discount.name))].join(', ')
                        : '',
                },
                availability: entry.products.reduce((a: any, o: any) => (o.meta.availability && a.push(o.meta.availability), a), [])
            }
        })),
        get statistics() {
            return {
                sum: Object.values(this.items).reduce((a: any, b: any) => monetary.sum(a, b.statistic), 0)
            }
        },
        get total() {
            return {
                sum: Object.values(this.items)
                    .map((item: any) => item.prices.total)
                    .reduce((a: any, b: any) => ({
                        price: monetary.sum(a.price, b.price),
                        discount: monetary.sum(a.discount, b.discount),
                        quantity: monetary.sum(a.quantity, b.quantity),
                        priceIncVat: monetary.sum(a.priceIncVat, b.priceIncVat),
                        priceExVat: monetary.sum(a.priceExVat, b.priceExVat),
                        priceVat: monetary.sum(a.priceVat, b.priceVat),
                        discountIncVat: monetary.sum(a.discountIncVat, b.discountIncVat),
                        discountExVat: monetary.sum(a.discountExVat, b.discountExVat),
                        discountVat: monetary.sum(a.discountVat, b.discountVat),
                    }), {
                        price: 0,
                        discount: 0,
                        quantity: 0,
                        priceIncVat: 0,
                        priceExVat: 0,
                        priceVat: 0,
                        discountIncVat: 0,
                        discountExVat: 0,
                        discountVat: 0
                    }),
                shipping: indicativeShipping.value
            }
        },
        get incomplete(): boolean {
            return Object.keys(this.items).length === 0 || Object.values(items).flatMap((item: any) => item.products).some(product => product.meta.availability?.availability === Availability.InsufficientStock);
        },
    }))

    const json: string | null = localStorage.getItem(key);

    if (json) {
        const local = JSON.parse(json);

        if (local.version === 2) {
            console.log('[Cart]: Assigning values from local storage.');
            Object.assign(items, local.items);
            last.value = local.last;
            timestamp.value = local.timestamp;
            culture.value = local.culture;
        }
    }

    const setLocalStorage = () => {
        console.log('[Cart]: Local storage update.');

        localStorage.setItem(key, JSON.stringify({
            items: items,
            last: last.value,
            timestamp: timestamp.value,
            culture: settings.value.language,
            version: 2
        }));
    }

    /**
     * Updates the `timestamp` reactive value with the current time in milliseconds.
     * This is typically used to signal that a cart-related event has occurred,
     * such as triggering a revalidation or refresh mechanism.
     */
    const setTimestamp = () => {
        console.log('[Cart]: Setting timestamp.')
        return timestamp.value = Date.now().valueOf();
    }

    /**
     * Watches for changes in the cart summary and updates:
     * - Logs cart summary to the console.
     * - Updates Matomo with current cart items and total sum.
     * - Removes items with quantity 0 from the internal items object.
     * - Saves updated cart state to local storage.
     */
    watch(timestamp, () => {
        console.log('[Cart]: Cart summary.');
        matomo.addOrRemoveEcommerceItems(cartSummary.value.items, cartSummary.value.statistics.sum);

        Object.entries(cartSummary.value.items)
            .filter(([key, item]: any) => item.quantity === 0)
            .forEach(([key]) => delete items[key]);

        setLocalStorage();
    });

    const add = async (entry: any) => {
        const key = [entry.article.id, [entry.products.map((p: any) => p.discriminator)]].toString();
        let item: any = items[key];

        if (item) {
            item.quantity += entry.quantity;

            if (item.quantity <= 0) {
                remove(key);
            }
        } else {
            item = items[key] = {
                article: {
                    id: entry.article.id,
                    code: entry.article.code,
                    type: entry.article.type,
                    name: entry.article.name || entry.article.description.name,
                    subtitle: entry.article.subtitle,
                    manufacturers: entry.article.manufacturers,
                    image: entry.article.image,
                    canonical: entry.article.canonical,
                    category: entry.article.url.substring(0, entry.article.url.lastIndexOf('/'))
                },
                products: entry.products.map((product: any) => ({
                    id: product.productId,
                    discriminator: product.discriminator,
                    code: product.productCode,
                    name: product.productName,
                    subtitle: product.subtitle,
                    manufacturer: product.manufacturer.name,
                    image: product.image,
                    quantity: product.quantity,
                    prices: product.prices,
                    weightInGrams: product.weightinGrams,
                    meta: {
                        discount: {
                            name: null,
                        },
                        availability: null
                    }
                })),
                quantity: entry.quantity
            };
        }

        last.value = items[key];

        if (entry.quantity > 0) {
            try {
                var products = entry.products.map((product: any) => ({
                    id: product.quantity === 1
                        ? product.productCode
                        : `${product.productCode}-MPX${product.quantity}`,
                    quantity: 1, // Quantity is 1 even for multipacks; item_price already reflects total price for the pack.
                    item_price: monetary.getPriceWith(product.prices, product.quantity, true)
                }));

                const eventData = {
                    content_ids: entry.products.map((product: any) => product.productCode),
                    content_type: 'product',
                    //content_category: props.breadcrumbs.slice(0, -1).map(bc => bc.title).join(' / '),
                    value: entry.products?.reduce((sum: number, product: any) => sum + (monetary.getPriceWith(product.prices, entry.quantity, true) || 0), 0) || 0,
                    currency: monetary.getCurrentCurrency(),
                    content_name: entry.article.name || entry.article.description.name,
                    contents: products
                };

                $fbq('track', 'AddToCart', eventData);
            } catch (e) {

            }
        }
    }

    const increase = (key: string, quantity: number) => {
        const item = {
            ...items[key],
            quantity: quantity
        }

        add(item);
    }

    const decrease = (key: string, quantity: number) => {
        increase(key, quantity * -1);
    }

    const remove: any = async (key: string) => {
        delete items[key];
    }

    const clear: any = async () => {
        for (const key in items) {
            delete items[key];
        }

        last.value = null;

        setTimestamp();
    }

    const getRefByName: any = (name: string) => {
        const refs: any = { last, timestamp, culture };
        return refs[name] || null
    }

    const validations = async (validations: Array<Validation>) => {
        console.log('[Cart]: Validations.');
        validations.forEach((validation: Validation) => {
            switch (validation.reason) {
                case 'Discount': {
                    validation.targets.forEach((target: Target) => {
                        const key: string = [target.articleId, [target.discriminator]].toString();
                        const item = items[key];

                        if (target.recipient === 'product') {
                            const product = item.products.find((product: any) => product.id === target.productId);

                            product.meta.discount.name = validation.name;
                            product[target.target] = target.value;
                        }
                    });
                    break;
                }
                case 'Disposable': {
                    validation.targets.forEach((target: Target) => {
                        const key: string = [target.articleId, [target.discriminator]].toString();
                        const item = items[key];

                        if (target.recipient === 'product') {
                            const product = item.products.find((product: any) => product.id === target.productId);

                            product.meta.availability = null;
                        }
                    });
                    break;
                }
                case 'Availability': {
                    validation.targets.forEach((target: Target) => {
                        const key: string = [target.articleId, [target.discriminator]].toString();
                        const item = items[key];

                        if (target.recipient === 'product') {
                            const product = item.products.find((product: any) => product.id === target.productId);
                            const name = item.article.type === 's' || item.article.type === 'g'
                                ? `${product.name} (${product.subtitle})`
                                : product.name;
                            
                            product.meta.availability = new AvailabilityMessage(
                                $i18n.t('$pipeline.app.validation.cart.availability.title', {
                                    name: name
                                }),
                                $i18n.t('$pipeline.app.validation.cart.availability.text', {
                                    name: name,
                                    code: product.code,
                                    ...target.value
                                }),
                                Availability.InsufficientStock);
                        }
                    });
                    break;
                }
                case 'AvailabilityOrderItem': {
                    validation.targets.forEach((target: any) => {
                        const key: string = [target.articleId, [target.discriminator]].toString();
                        const item = items[key];

                        if (target.recipient === 'product') {
                            const product = item.products.find((product: any) => product.id === target.productId);

                            product.meta.availability = new AvailabilityMessage(
                                $i18n.t('$pipeline.app.validation.cart.availability-order-item.title', {
                                    name: product.name,
                                    code: product.code,
                                    ...target.value
                                }),
                                $i18n.t('$pipeline.app.validation.cart.availability-order-item.text', {
                                    name: product.name,
                                    code: product.code,
                                    ...target.value
                                }),
                                Availability.OrderItem);
                        }
                    });
                    break;
                }
                case 'Changed': {
                    validation.targets.forEach((target: Target) => {
                        const key: string = [target.articleId, [target.discriminator]].toString();
                        const item = items[key];

                        const recipient = (() => {
                            switch (target.recipient) {
                                case 'article': return item.article;
                                case 'product': return item.products.find((product: any) => product.id === target.productId);
                                case 'cart': return getRefByName(target.target)
                                default:
                                    break;
                            }
                        })();

                        if (target.recipient === 'cart') {
                            recipient.value = target.value;
                        } else {
                            recipient[`${target.target.charAt(0).toLowerCase()}${target.target.slice(1)}`] = target.value;
                        }
                    });
                    break;
                }
                case 'IndicativeShipping': {
                    validation.targets.forEach((target: Target) => {
                        if (target.recipient === 'cart' && target.target === 'prices' && target.value) {
                            indicativeShipping.value = monetary.getPriceFor(target.value[settings.value.country].find((price: any) => price.currency === settings.value.currency), 1, true);
                        } else {
                            indicativeShipping.value = null;
                        }
                    });
                    break;
                }
                default:
                    break;
            }
        });

        setTimestamp();
    }

    const getRaw = () => ({
        culture: $i18n.locale.value,
        items: toRaw(items)
    });

    return {
        cartManager: {
            items,
            last,
            culture,
            add,
            increase,
            decrease,
            remove,
            clear,
            loading,
            setTimestamp,
            validations,
            getRaw
        },
        timestamp,
        cart: cartSummary
    };
});